import { useRef, useMemo, useCallback } from 'react'
import * as THREE from 'three'
import type { PrimaryMetricConfig } from '../config/dashboardPanelDefaults'
import { parseMetricValue } from '../config/dashboardPanelDefaults'
import type { PanelOverride } from '../stores/useDashboardPanelStore'

const BASE_CANVAS_WIDTH = 512
const FONT_FAMILY = "'Satoshi', sans-serif"

// --- Canvas 2D drawing helpers ---

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function computeCanvasWidth(panelWidth: number, baseWidth: number): number {
  const raw = Math.round((BASE_CANVAS_WIDTH * panelWidth) / baseWidth)
  return Math.max(256, Math.min(1024, raw))
}

function computeCanvasHeight(panelHeight: number, baseWidth: number): number {
  const raw = Math.round((BASE_CANVAS_WIDTH * panelHeight) / baseWidth)
  return Math.max(256, Math.min(1024, raw))
}

/** Draw value and unit inline, centered at (cx, y). Returns total line height. */
function drawInlineValueUnit(
  ctx: CanvasRenderingContext2D,
  cx: number,
  y: number,
  valStr: string,
  unitStr: string,
  valueFontSize: number,
  unitFontSize: number,
  textColor: string,
  accentColor: string
) {
  ctx.textBaseline = 'middle'

  // Measure value
  ctx.font = `bold ${valueFontSize}px ${FONT_FAMILY}`
  const valW = ctx.measureText(valStr).width

  // Measure unit
  ctx.font = `500 ${unitFontSize}px ${FONT_FAMILY}`
  const unitW = ctx.measureText(unitStr).width

  const gap = 4
  const totalW = valW + gap + unitW
  const startX = cx - totalW / 2

  // Draw value
  ctx.font = `bold ${valueFontSize}px ${FONT_FAMILY}`
  ctx.textAlign = 'left'
  ctx.fillStyle = textColor
  ctx.fillText(valStr, startX, y)

  // Draw unit
  ctx.font = `500 ${unitFontSize}px ${FONT_FAMILY}`
  ctx.textAlign = 'left'
  ctx.fillStyle = accentColor
  ctx.fillText(unitStr, startX + valW + gap, y)

  // Reset alignment
  ctx.textAlign = 'center'
}

// --- Visual indicator drawing functions ---

function drawBatteryIndicator(
  ctx: CanvasRenderingContext2D,
  cx: number,
  areaTop: number,
  areaBottom: number,
  value: number,
  metricConfig: PrimaryMetricConfig,
  accentColor: string,
  textColor: string,
  config: Required<PanelOverride>
) {
  const b = config.battery
  const normalized = Math.max(
    0,
    Math.min(1, (value - metricConfig.gaugeMin) / (metricConfig.gaugeMax - metricConfig.gaugeMin))
  )
  const areaH = areaBottom - areaTop

  const valStr = value.toFixed(0)
  const unitStr = metricConfig.gaugeUnit
  const labelStr = metricConfig.label.toUpperCase()

  if (b.orientation === 'horizontal') {
    // --- Horizontal battery ---
    const batH = b.iconWidth
    const batW = Math.min(areaH * b.iconHeightRatio, 250)

    // Compute total content height for vertical centering
    const textBlockH = b.valueFontSize + b.labelFontSize * 1.5
    const totalH = batH + b.iconTextGap + textBlockH
    const startY = areaTop + (areaH - totalH) / 2

    const batX = cx - batW / 2
    const batY = startY

    // Cap (right side nub)
    ctx.fillStyle = hexToRgba(accentColor, 0.3)
    roundRect(ctx, batX + batW - 2, batY + batH / 2 - b.capWidth / 2, b.capHeight, b.capWidth, 3)
    ctx.fill()

    // Outline
    ctx.strokeStyle = hexToRgba(accentColor, 0.4)
    ctx.lineWidth = 2
    roundRect(ctx, batX, batY, batW, batH, b.cornerRadius)
    ctx.stroke()

    // Fill (from left)
    const fillMaxW = batW - b.fillPadding * 2
    const fillW = fillMaxW * normalized
    if (fillW > 0) {
      ctx.fillStyle = hexToRgba(accentColor, 0.6)
      roundRect(
        ctx,
        batX + b.fillPadding,
        batY + b.fillPadding,
        fillW,
        batH - b.fillPadding * 2,
        Math.max(0, b.cornerRadius - b.fillPadding)
      )
      ctx.fill()
    }

    // Inline value+unit
    const textY = batY + batH + b.iconTextGap + b.valueFontSize / 2
    drawInlineValueUnit(
      ctx,
      cx,
      textY,
      valStr,
      unitStr,
      b.valueFontSize,
      b.unitFontSize,
      textColor,
      accentColor
    )

    // Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.font = `500 ${b.labelFontSize}px ${FONT_FAMILY}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.letterSpacing = '1px'
    ctx.fillText(labelStr, cx, textY + b.valueFontSize * 0.5 + b.labelFontSize)
    ctx.letterSpacing = '0px'
  } else {
    // --- Vertical battery (default) ---
    const batW = b.iconWidth
    const batH = Math.min(areaH * b.iconHeightRatio, 250)

    // Compute total content height for vertical centering
    const textBlockH = b.valueFontSize + b.labelFontSize * 1.5
    const totalH = b.capHeight + batH + b.iconTextGap + textBlockH
    const startY = areaTop + (areaH - totalH) / 2

    const batX = cx - batW / 2
    const batY = startY + b.capHeight

    // Cap (top center nub)
    ctx.fillStyle = hexToRgba(accentColor, 0.3)
    roundRect(ctx, cx - b.capWidth / 2, batY - b.capHeight + 2, b.capWidth, b.capHeight, 3)
    ctx.fill()

    // Outline
    ctx.strokeStyle = hexToRgba(accentColor, 0.4)
    ctx.lineWidth = 2
    roundRect(ctx, batX, batY, batW, batH, b.cornerRadius)
    ctx.stroke()

    // Fill (from bottom)
    const fillMaxH = batH - b.fillPadding * 2
    const fillH = fillMaxH * normalized
    if (fillH > 0) {
      ctx.fillStyle = hexToRgba(accentColor, 0.6)
      const fillY = batY + b.fillPadding + fillMaxH - fillH
      roundRect(
        ctx,
        batX + b.fillPadding,
        fillY,
        batW - b.fillPadding * 2,
        fillH,
        Math.max(0, b.cornerRadius - b.fillPadding)
      )
      ctx.fill()
    }

    // Inline value+unit
    const textY = batY + batH + b.iconTextGap + b.valueFontSize / 2
    drawInlineValueUnit(
      ctx,
      cx,
      textY,
      valStr,
      unitStr,
      b.valueFontSize,
      b.unitFontSize,
      textColor,
      accentColor
    )

    // Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.font = `500 ${b.labelFontSize}px ${FONT_FAMILY}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.letterSpacing = '1px'
    ctx.fillText(labelStr, cx, textY + b.valueFontSize * 0.5 + b.labelFontSize)
    ctx.letterSpacing = '0px'
  }
}

function drawThermometerIndicator(
  ctx: CanvasRenderingContext2D,
  cx: number,
  areaTop: number,
  areaBottom: number,
  value: number,
  metricConfig: PrimaryMetricConfig,
  accentColor: string,
  textColor: string,
  config: Required<PanelOverride>
) {
  const th = config.thermometer
  const normalized = Math.max(
    0,
    Math.min(1, (value - metricConfig.gaugeMin) / (metricConfig.gaugeMax - metricConfig.gaugeMin))
  )
  const areaH = areaBottom - areaTop

  const valStr = value.toFixed(0)
  const unitStr = `°${metricConfig.gaugeUnit}`
  const labelStr = metricConfig.label.toUpperCase()

  if (th.orientation === 'horizontal') {
    // --- Horizontal thermometer ---
    const tubeW = Math.min(areaH * th.tubeHeightRatio, 200)
    const tubeH = th.tubeWidth

    // Compute total content height for vertical centering
    const iconH = Math.max(tubeH, th.bulbRadius * 2)
    const textBlockH = th.valueFontSize + th.labelFontSize * 1.5
    const totalH = iconH + th.iconTextGap + textBlockH
    const startY = areaTop + (areaH - totalH) / 2

    const bulbCX = cx - tubeW / 2 - th.bulbRadius * 0.6
    const tubeLeft = cx - tubeW / 2
    const tubeTop = startY + (iconH - tubeH) / 2

    // Tube outline
    ctx.strokeStyle = hexToRgba(accentColor, 0.4)
    ctx.lineWidth = 2
    roundRect(ctx, tubeLeft, tubeTop, tubeW, tubeH, tubeH / 2)
    ctx.stroke()

    // Bulb outline
    ctx.beginPath()
    ctx.arc(bulbCX, startY + iconH / 2, th.bulbRadius, 0, Math.PI * 2)
    ctx.strokeStyle = hexToRgba(accentColor, 0.4)
    ctx.lineWidth = 2
    ctx.stroke()

    // Bulb fill
    ctx.beginPath()
    ctx.arc(bulbCX, startY + iconH / 2, th.bulbRadius - 3, 0, Math.PI * 2)
    ctx.fillStyle = hexToRgba(accentColor, 0.6)
    ctx.fill()

    // Tube fill (from left)
    const fillMaxW = tubeW - th.fillPadding * 2
    const fillW = fillMaxW * normalized
    if (fillW > 0) {
      roundRect(
        ctx,
        tubeLeft + th.fillPadding,
        tubeTop + th.fillPadding,
        fillW + th.fillPadding,
        tubeH - th.fillPadding * 2,
        (tubeH - th.fillPadding * 2) / 2
      )
      ctx.fillStyle = hexToRgba(accentColor, 0.6)
      ctx.fill()
    }

    // Inline value+unit
    const textY = startY + iconH + th.iconTextGap + th.valueFontSize / 2
    drawInlineValueUnit(
      ctx,
      cx,
      textY,
      valStr,
      unitStr,
      th.valueFontSize,
      th.unitFontSize,
      textColor,
      accentColor
    )

    // Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.font = `500 ${th.labelFontSize}px ${FONT_FAMILY}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.letterSpacing = '1px'
    ctx.fillText(labelStr, cx, textY + th.valueFontSize * 0.5 + th.labelFontSize)
    ctx.letterSpacing = '0px'
  } else {
    // --- Vertical thermometer (default) ---
    const tubeW = th.tubeWidth
    const tubeH = Math.min(areaH * th.tubeHeightRatio, 200)
    const iconH = tubeH + th.bulbRadius * 1.6 // tube + bulb

    // Compute total content height for vertical centering
    const textBlockH = th.valueFontSize + th.labelFontSize * 1.5
    const totalH = iconH + th.iconTextGap + textBlockH
    const startY = areaTop + (areaH - totalH) / 2

    const tubeX = cx - tubeW / 2
    const tubeTop = startY
    const tubeBottom = tubeTop + tubeH
    const bulbCY = tubeBottom + th.bulbRadius * 0.6

    // Tube outline
    ctx.strokeStyle = hexToRgba(accentColor, 0.4)
    ctx.lineWidth = 2
    roundRect(ctx, tubeX, tubeTop, tubeW, tubeH, tubeW / 2)
    ctx.stroke()

    // Bulb outline
    ctx.beginPath()
    ctx.arc(cx, bulbCY, th.bulbRadius, 0, Math.PI * 2)
    ctx.strokeStyle = hexToRgba(accentColor, 0.4)
    ctx.lineWidth = 2
    ctx.stroke()

    // Bulb fill
    ctx.beginPath()
    ctx.arc(cx, bulbCY, th.bulbRadius - 3, 0, Math.PI * 2)
    ctx.fillStyle = hexToRgba(accentColor, 0.6)
    ctx.fill()

    // Tube fill (from bottom)
    const fillMaxH = tubeH - th.fillPadding * 2
    const fillH = fillMaxH * normalized
    if (fillH > 0) {
      const fillY = tubeTop + th.fillPadding + fillMaxH - fillH
      roundRect(
        ctx,
        tubeX + th.fillPadding,
        fillY,
        tubeW - th.fillPadding * 2,
        fillH + th.fillPadding,
        (tubeW - th.fillPadding * 2) / 2
      )
      ctx.fillStyle = hexToRgba(accentColor, 0.6)
      ctx.fill()
    }

    // Inline value+unit
    const textY = bulbCY + th.bulbRadius + th.iconTextGap + th.valueFontSize / 2
    drawInlineValueUnit(
      ctx,
      cx,
      textY,
      valStr,
      unitStr,
      th.valueFontSize,
      th.unitFontSize,
      textColor,
      accentColor
    )

    // Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.font = `500 ${th.labelFontSize}px ${FONT_FAMILY}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.letterSpacing = '1px'
    ctx.fillText(labelStr, cx, textY + th.valueFontSize * 0.5 + th.labelFontSize)
    ctx.letterSpacing = '0px'
  }
}

function drawGaugeIndicator(
  ctx: CanvasRenderingContext2D,
  cx: number,
  areaTop: number,
  areaBottom: number,
  value: number,
  metricConfig: PrimaryMetricConfig,
  accentColor: string,
  textColor: string,
  config: Required<PanelOverride>
) {
  const g = config.gauge
  const areaH = areaBottom - areaTop
  const radius = Math.min(areaH * 0.3, 90)

  // Compute total content height for vertical centering
  // Gauge: arc area (diameter) + gap below arc + value line + label
  const gaugeVisualH = radius * 2
  const textBlockH = g.valueFontSize + g.labelFontSize * 1.5
  const totalH = gaugeVisualH + g.iconTextGap + textBlockH
  const startY = areaTop + (areaH - totalH) / 2

  const gaugeCY = startY + radius

  // Arc angles derived from config arcAngle
  const arcRad = (g.arcAngle * Math.PI) / 180
  const halfArc = arcRad / 2
  const startAngle = Math.PI * 1.5 - halfArc
  const endAngle = Math.PI * 1.5 + halfArc
  const totalArc = endAngle - startAngle

  // Normalize: center is at 0, min is left, max is right
  const min = metricConfig.gaugeMin
  const max = metricConfig.gaugeMax
  const clamped = Math.max(min, Math.min(max, value))
  const normalized = (clamped - min) / (max - min)

  // Zero position on the arc
  const zeroNorm = (0 - min) / (max - min)
  const zeroAngle = startAngle + totalArc * zeroNorm

  // Background track
  ctx.beginPath()
  ctx.arc(cx, gaugeCY, radius, startAngle, endAngle)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)'
  ctx.lineWidth = g.trackWidth
  ctx.lineCap = 'round'
  ctx.stroke()

  // Zero tick mark
  const tickLen = 10
  const zeroOuterX = cx + (radius + tickLen / 2) * Math.cos(zeroAngle)
  const zeroOuterY = gaugeCY + (radius + tickLen / 2) * Math.sin(zeroAngle)
  const zeroInnerX = cx + (radius - tickLen / 2) * Math.cos(zeroAngle)
  const zeroInnerY = gaugeCY + (radius - tickLen / 2) * Math.sin(zeroAngle)
  ctx.beginPath()
  ctx.moveTo(zeroInnerX, zeroInnerY)
  ctx.lineTo(zeroOuterX, zeroOuterY)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.stroke()

  // Value arc (from zero to value position)
  const valueAngle = startAngle + totalArc * normalized
  if (Math.abs(valueAngle - zeroAngle) > 0.01) {
    ctx.beginPath()
    if (value >= 0) {
      ctx.arc(cx, gaugeCY, radius, zeroAngle, valueAngle)
    } else {
      ctx.arc(cx, gaugeCY, radius, valueAngle, zeroAngle)
    }
    ctx.strokeStyle = accentColor
    ctx.lineWidth = g.trackWidth
    ctx.lineCap = 'round'
    ctx.stroke()
  }

  // Needle pointer
  const needleR = radius * g.needleLength
  const needleTipX = cx + needleR * Math.cos(valueAngle)
  const needleTipY = gaugeCY + needleR * Math.sin(valueAngle)
  const perpAngle = valueAngle + Math.PI / 2
  const halfW = g.needleWidth / 2
  const baseX1 = cx + halfW * Math.cos(perpAngle)
  const baseY1 = gaugeCY + halfW * Math.sin(perpAngle)
  const baseX2 = cx - halfW * Math.cos(perpAngle)
  const baseY2 = gaugeCY - halfW * Math.sin(perpAngle)

  ctx.beginPath()
  ctx.moveTo(baseX1, baseY1)
  ctx.lineTo(needleTipX, needleTipY)
  ctx.lineTo(baseX2, baseY2)
  ctx.closePath()
  ctx.fillStyle = hexToRgba(accentColor, 0.8)
  ctx.fill()

  // Center pivot circle
  ctx.beginPath()
  ctx.arc(cx, gaugeCY, 5, 0, Math.PI * 2)
  ctx.fillStyle = accentColor
  ctx.fill()

  // Inline value+unit below gauge (same position as battery text)
  const valStr = value.toFixed(0)
  const unitStr = metricConfig.gaugeUnit
  const textY = startY + gaugeVisualH + g.iconTextGap + g.valueFontSize / 2
  drawInlineValueUnit(
    ctx,
    cx,
    textY,
    valStr,
    unitStr,
    g.valueFontSize,
    g.unitFontSize,
    textColor,
    accentColor
  )

  // Label below value
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
  ctx.font = `500 ${g.labelFontSize}px ${FONT_FAMILY}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.letterSpacing = '1px'
  ctx.fillText(
    metricConfig.label.toUpperCase(),
    cx,
    textY + g.valueFontSize * 0.5 + g.labelFontSize
  )
  ctx.letterSpacing = '0px'
}

function drawNumberIndicator(
  ctx: CanvasRenderingContext2D,
  cx: number,
  areaTop: number,
  areaBottom: number,
  value: number,
  metricConfig: PrimaryMetricConfig,
  accentColor: string,
  textColor: string
) {
  const areaH = areaBottom - areaTop
  const centerY = areaTop + areaH * 0.42

  // Inline value+unit
  const valStr = value.toFixed(0)
  const unitStr = metricConfig.gaugeUnit
  drawInlineValueUnit(ctx, cx, centerY, valStr, unitStr, 48, 20, textColor, accentColor)

  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
  ctx.font = `500 13px ${FONT_FAMILY}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.letterSpacing = '1px'
  ctx.fillText(metricConfig.label.toUpperCase(), cx, centerY + 48)
  ctx.letterSpacing = '0px'
}

// Dispatch to the right drawing function
function drawMetricColumn(
  ctx: CanvasRenderingContext2D,
  cx: number,
  areaTop: number,
  areaBottom: number,
  value: number,
  metricConfig: PrimaryMetricConfig,
  accentColor: string,
  textColor: string,
  config: Required<PanelOverride>
) {
  switch (metricConfig.visualType) {
    case 'battery':
      drawBatteryIndicator(
        ctx,
        cx,
        areaTop,
        areaBottom,
        value,
        metricConfig,
        accentColor,
        textColor,
        config
      )
      break
    case 'thermometer':
      drawThermometerIndicator(
        ctx,
        cx,
        areaTop,
        areaBottom,
        value,
        metricConfig,
        accentColor,
        textColor,
        config
      )
      break
    case 'gauge':
      drawGaugeIndicator(
        ctx,
        cx,
        areaTop,
        areaBottom,
        value,
        metricConfig,
        accentColor,
        textColor,
        config
      )
      break
    default:
      drawNumberIndicator(ctx, cx, areaTop, areaBottom, value, metricConfig, accentColor, textColor)
      break
  }
}

function drawDashboard(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  heading: string,
  summary: { label: string; value: string }[],
  metrics: PrimaryMetricConfig[],
  config: Required<PanelOverride>
) {
  const pad = config.padding

  // Clear
  ctx.clearRect(0, 0, width, height)

  // Background — frosted glass style
  ctx.fillStyle = hexToRgba(config.bgColor, config.bgOpacity)
  roundRect(ctx, 4, 4, width - 8, height - 8, 18)
  ctx.fill()

  // Subtle border — white at 10%
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
  ctx.lineWidth = 1.5
  roundRect(ctx, 4, 4, width - 8, height - 8, 18)
  ctx.stroke()

  // Heading
  const headerY = pad + 24
  ctx.fillStyle = config.textColor
  ctx.font = `600 26px ${FONT_FAMILY}`
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(heading, pad, headerY)

  // Separator
  const sepY = headerY + 20
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(pad, sepY)
  ctx.lineTo(width - pad, sepY)
  ctx.stroke()

  // Metrics area
  const metricsTop = sepY + 12
  const metricsBottom = height - pad

  if (metrics.length === 1) {
    const m = metrics[0]
    const match = summary.find((s) => s.label === m.label)
    const val = match ? parseMetricValue(match.value) : 0
    drawMetricColumn(
      ctx,
      width / 2,
      metricsTop,
      metricsBottom,
      val,
      m,
      config.accentColor,
      config.textColor,
      config
    )
  } else if (metrics.length >= 2) {
    const colWidth = (width - pad * 2) / 2

    // Thin vertical divider
    const divX = width / 2
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(divX, metricsTop + 8)
    ctx.lineTo(divX, metricsBottom - 8)
    ctx.stroke()

    for (let i = 0; i < 2; i++) {
      const m = metrics[i]
      const match = summary.find((s) => s.label === m.label)
      const val = match ? parseMetricValue(match.value) : 0
      const colCx = pad + colWidth * i + colWidth / 2
      drawMetricColumn(
        ctx,
        colCx,
        metricsTop,
        metricsBottom,
        val,
        m,
        config.accentColor,
        config.textColor,
        config
      )
    }
  }
}

// --- Hook ---

export interface DashboardPanelTextureResult {
  texture: THREE.CanvasTexture
  redraw: (
    config: Required<PanelOverride>,
    summary: { label: string; value: string }[],
    metrics: PrimaryMetricConfig[],
    heading: string
  ) => void
}

export function useDashboardPanelTexture(
  initConfig: Required<PanelOverride>,
  initSummary: { label: string; value: string }[],
  metrics: PrimaryMetricConfig[],
  heading: string,
  baseWidth: number = 3
): DashboardPanelTextureResult {
  const lastDrawRef = useRef<string>('')
  const lastCanvasWidthRef = useRef<number>(0)
  const lastCanvasHeightRef = useRef<number>(0)

  const texture = useMemo(() => {
    const canvasW = computeCanvasWidth(initConfig.panelWidth, baseWidth)
    const canvasH = computeCanvasHeight(initConfig.panelHeight, baseWidth)
    const canvas = document.createElement('canvas')
    canvas.width = canvasW
    canvas.height = canvasH
    lastCanvasWidthRef.current = canvasW
    lastCanvasHeightRef.current = canvasH
    const ctx = canvas.getContext('2d')!

    drawDashboard(ctx, canvasW, canvasH, heading, initSummary, metrics, initConfig)
    lastDrawRef.current = JSON.stringify({
      summary: initSummary,
      config: initConfig,
      heading,
    })

    const tex = new THREE.CanvasTexture(canvas)
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter
    tex.generateMipmaps = false
    return tex
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const redraw = useCallback(
    (
      config: Required<PanelOverride>,
      summary: { label: string; value: string }[],
      ms: PrimaryMetricConfig[],
      h: string
    ) => {
      const canvas = texture.image as HTMLCanvasElement
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Resize canvas if panelWidth or panelHeight changed
      const targetW = computeCanvasWidth(config.panelWidth, baseWidth)
      const targetH = computeCanvasHeight(config.panelHeight, baseWidth)
      if (targetW !== lastCanvasWidthRef.current || targetH !== lastCanvasHeightRef.current) {
        canvas.width = targetW
        canvas.height = targetH
        lastCanvasWidthRef.current = targetW
        lastCanvasHeightRef.current = targetH
        // Force full redraw after resize
        lastDrawRef.current = ''
      }

      const drawKey = JSON.stringify({ summary, config, heading: h })
      if (drawKey === lastDrawRef.current) return
      lastDrawRef.current = drawKey

      drawDashboard(ctx, canvas.width, canvas.height, h, summary, ms, config)
      texture.needsUpdate = true
    },
    [texture, baseWidth]
  )

  return { texture, redraw }
}
